import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, MapPin, Star, Shield, Clock, ChevronRight, Building2, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const { user, login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [agencies, setAgencies] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed data first
        await axios.post(`${API}/seed`);
        
        // Fetch cities and agencies
        const [citiesRes, agenciesRes] = await Promise.all([
          axios.get(`${API}/cities`),
          axios.get(`${API}/agencies`)
        ]);
        
        setCities(citiesRes.data.cities);
        setAgencies(agenciesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleCityChange = async (city) => {
    setSelectedCity(city);
    try {
      const response = await axios.get(`${API}/agencies`, {
        params: city && city !== 'all' ? { city } : {}
      });
      setAgencies(response.data);
    } catch (error) {
      console.error('Error filtering agencies:', error);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="sticky top-0 bg-white border-b border-slate-200 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="font-display text-xl font-bold text-slate-900 tracking-tight" data-testid="logo">
              My Detective
            </Link>
            
            <div className="flex items-center gap-4">
              {isAuthenticated ? (
                <>
                  <Link 
                    to={user?.role === 'agency' ? '/agency/dashboard' : '/dashboard'}
                    className="text-sm text-slate-600 hover:text-slate-900"
                    data-testid="dashboard-link"
                  >
                    Dashboard
                  </Link>
                  <div className="h-8 w-8 rounded-full bg-slate-200 overflow-hidden">
                    {user?.picture && <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />}
                  </div>
                </>
              ) : (
                <>
                  <Button 
                    variant="ghost" 
                    onClick={() => login('agency')}
                    className="text-sm text-slate-600 hover:text-slate-900"
                    data-testid="for-agencies-btn"
                  >
                    For Agencies
                  </Button>
                  <Button 
                    onClick={() => login('user')}
                    className="bg-slate-900 text-white hover:bg-slate-800 rounded-none"
                    data-testid="login-btn"
                  >
                    Login
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-4">
                India&apos;s Trusted Platform
              </p>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 tracking-tighter mb-6" data-testid="hero-title">
                Hire Professional Detectives
              </h1>
              <p className="text-base lg:text-lg text-slate-600 leading-relaxed mb-8 max-w-lg">
                Background checks for marriage, corporate investigations, and personal matters. 
                Connect with verified detective agencies across India.
              </p>
              
              {/* City Selector */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <Select value={selectedCity} onValueChange={handleCityChange} data-testid="city-selector">
                    <SelectTrigger className="w-full h-12 rounded-none border-slate-200 bg-white" data-testid="city-selector-trigger">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        <SelectValue placeholder="Select your city" />
                      </div>
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="all">All Cities</SelectItem>
                      {cities.map((city) => (
                        <SelectItem key={city} value={city}>{city}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  className="h-12 px-8 bg-slate-900 text-white hover:bg-slate-800 rounded-none"
                  onClick={() => document.getElementById('agencies').scrollIntoView({ behavior: 'smooth' })}
                  data-testid="search-agencies-btn"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Search Agencies
                </Button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 mt-12 pt-8 border-t border-slate-200">
                <div>
                  <p className="font-display text-3xl font-bold text-slate-900">500+</p>
                  <p className="text-sm text-slate-500">Verified Agencies</p>
                </div>
                <div>
                  <p className="font-display text-3xl font-bold text-slate-900">50k+</p>
                  <p className="text-sm text-slate-500">Cases Solved</p>
                </div>
                <div>
                  <p className="font-display text-3xl font-bold text-slate-900">100+</p>
                  <p className="text-sm text-slate-500">Cities Covered</p>
                </div>
              </div>
            </div>

            {/* Hero Image */}
            <div className="hidden lg:block">
              <div className="border border-slate-200">
                <img
                  src="https://images.unsplash.com/photo-1697301439916-169bd6844842?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxtYWduaWZ5aW5nJTIwZ2xhc3MlMjBkb2N1bWVudHxlbnwwfHx8fDE3NzU2ODE3MzB8MA&ixlib=rb-4.1.0&q=85"
                  alt="Detective investigation"
                  className="w-full h-[400px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2">Our Services</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">
              Investigation Services
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: <Users className="w-6 h-6" />,
                title: 'Pre-Matrimonial',
                desc: 'Background verification of prospective bride or groom and their family'
              },
              {
                icon: <Building2 className="w-6 h-6" />,
                title: 'Corporate',
                desc: 'Employee verification, fraud investigation, and corporate espionage'
              },
              {
                icon: <Shield className="w-6 h-6" />,
                title: 'Personal',
                desc: 'Surveillance, missing persons, and personal background checks'
              }
            ].map((service) => (
              <div 
                key={service.title} 
                className="bg-white border border-slate-200 p-6 hover:border-slate-400 transition-colors"
                data-testid={`service-card-${service.title.toLowerCase()}`}
              >
                <div className="w-12 h-12 bg-slate-100 flex items-center justify-center mb-4">
                  {service.icon}
                </div>
                <h3 className="font-display text-lg font-bold text-slate-900 mb-2">{service.title}</h3>
                <p className="text-sm text-slate-600">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Agencies Section */}
      <section id="agencies" className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-2">
                {selectedCity && selectedCity !== 'all' ? `Agencies in ${selectedCity}` : 'Featured Agencies'}
              </p>
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
                Verified Detective Agencies
              </h2>
            </div>
            
            <Select value={selectedCity} onValueChange={handleCityChange}>
              <SelectTrigger className="w-48 rounded-none border-slate-200" data-testid="filter-city-selector">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <SelectValue placeholder="Filter by city" />
                </div>
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all">All Cities</SelectItem>
                {cities.map((city) => (
                  <SelectItem key={city} value={city}>{city}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white border border-slate-200 p-6 animate-pulse">
                  <div className="h-4 bg-slate-200 w-3/4 mb-4"></div>
                  <div className="h-3 bg-slate-200 w-1/2 mb-6"></div>
                  <div className="h-8 bg-slate-200 w-full"></div>
                </div>
              ))}
            </div>
          ) : agencies.length === 0 ? (
            <div className="text-center py-12 bg-white border border-slate-200">
              <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">No agencies found in this city</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {agencies.map((agency) => (
                <div 
                  key={agency.agency_id} 
                  className="bg-white border border-slate-200 hover:border-slate-400 transition-colors"
                  data-testid={`agency-card-${agency.agency_id}`}
                >
                  {/* Card Header */}
                  <div className="p-6 border-b border-slate-200">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-display text-lg font-bold text-slate-900">{agency.name}</h3>
                        <div className="flex items-center gap-1 text-sm text-slate-500">
                          <MapPin className="w-3 h-3" />
                          {agency.city}
                        </div>
                      </div>
                      {agency.verified && (
                        <div className="flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 border border-emerald-200">
                          <Shield className="w-3 h-3" />
                          Verified
                        </div>
                      )}
                    </div>
                    
                    {/* Rating */}
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                        <span className="font-medium text-slate-900">{agency.rating}</span>
                      </div>
                      <span className="text-sm text-slate-500">({agency.total_reviews} reviews)</span>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-6">
                    {/* Specializations */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {agency.specializations?.slice(0, 3).map((spec, idx) => (
                        <span 
                          key={idx}
                          className="text-xs text-slate-600 bg-slate-100 px-2 py-1"
                        >
                          {spec}
                        </span>
                      ))}
                    </div>

                    {/* Experience & Price */}
                    <div className="flex items-center justify-between text-sm mb-4">
                      <div className="flex items-center gap-1 text-slate-500">
                        <Clock className="w-4 h-4" />
                        {agency.years_experience} years exp.
                      </div>
                      <div className="font-medium text-slate-900">
                        From {formatPrice(agency.base_price)}
                      </div>
                    </div>

                    {/* CTA */}
                    <Button 
                      onClick={() => navigate(`/agency/${agency.agency_id}`)}
                      className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-none"
                      data-testid={`book-agency-${agency.agency_id}`}
                    >
                      View Details
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-4">
            Are you a Detective Agency?
          </h2>
          <p className="text-slate-400 mb-8 max-w-lg mx-auto">
            Join India&apos;s largest platform for detective services. Get verified and reach thousands of clients.
          </p>
          <Button 
            onClick={() => login('agency')}
            className="bg-white text-slate-900 hover:bg-slate-100 rounded-none px-8"
            data-testid="register-agency-btn"
          >
            Register Your Agency
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="font-display text-lg font-bold text-slate-900">My Detective</p>
            <p className="text-sm text-slate-500">© 2025 My Detective. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
